import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { TERMS_PRIVACY_LINKS } from '@/constants'
import {
  ASSISTANT_APPROVAL_TTL_MS,
  type AssistantApprovalCard as ApprovalCardData,
  type AssistantApprovalCheckbox,
} from '../contract'
import { AssistantReferralPreview } from '../results/AssistantReferralDraftCard'

/** Where the card is in its life: waiting, or what the user decided. */
export type AssistantApprovalPhase =
  | 'pending'
  | 'approved'
  | 'cancelled'
  | 'done'
  | 'failed'

const OUTCOME_TEXT: Record<
  Exclude<AssistantApprovalPhase, 'pending'>,
  string
> = {
  approved: 'Approved. Working on it…',
  cancelled: 'Cancelled. Nothing was changed.',
  done: 'Approved and done.',
  failed: 'Approved, but it didn’t go through. See the note below.',
}

export const isApprovalCardExpired = (card: ApprovalCardData, now: number) => {
  const issuedAt = Date.parse(card.issuedAt)
  return Number.isNaN(issuedAt) || now - issuedAt > ASSISTANT_APPROVAL_TTL_MS
}

const CheckboxLabel = ({ box }: { box: AssistantApprovalCheckbox }) => (
  <Typography component="span" variant="caption">
    {box.label}
    {box.termsLinks && (
      <>
        {' '}
        (
        <Link href={TERMS_PRIVACY_LINKS.terms} target="_blank">
          Terms and Conditions
        </Link>
        ,{' '}
        <Link href={TERMS_PRIVACY_LINKS.privacy} target="_blank">
          Privacy Policy
        </Link>
        )
      </>
    )}
  </Typography>
)

interface AssistantApprovalCardProps {
  card: ApprovalCardData
  phase: AssistantApprovalPhase
  onApprove: () => void
  onCancel: () => void
  /** Another request is running; decisions wait until it finishes. */
  disabled?: boolean
}

/**
 * The Approve/Cancel card for a change. Every word comes from the card the
 * server wrote from real data; the model's prose never reaches it. Approve
 * stays locked until every required statement is ticked.
 */
const AssistantApprovalCard = ({
  card,
  phase,
  onApprove,
  onCancel,
  disabled,
}: AssistantApprovalCardProps) => {
  const [ticked, setTicked] = useState<Record<string, boolean>>({})
  const isPending = phase === 'pending'
  const isExpired = isPending && isApprovalCardExpired(card, Date.now())
  const allTicked = card.checkboxes.every(box => ticked[box.id])

  return (
    <Box
      data-testid="assistant-approval-card"
      role="group"
      aria-label={card.title}
      sx={{
        // The same compact bordered card as the answer cards; a pending
        // change is marked by a navy edge.
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        borderLeft: isPending ? 3 : 1,
        borderLeftColor: isPending ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant="body2" fontWeight={700}>
        {card.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {card.summary}
      </Typography>
      {card.rows.length > 0 && (
        <Box component="dl" sx={{ m: 0 }}>
          {card.rows.map(row => (
            <Box key={row.label} sx={{ display: 'flex', gap: 1, py: 0.25 }}>
              <Typography
                component="dt"
                variant="caption"
                color="text.secondary"
                sx={{ width: 110, flexShrink: 0 }}
              >
                {row.label}
              </Typography>
              <Typography
                component="dd"
                variant="caption"
                sx={{ m: 0, flex: 1, overflowWrap: 'anywhere' }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {card.userText && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {card.userText.label}
          </Typography>
          <Typography
            variant="body2"
            data-testid="assistant-approval-user-text"
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {card.userText.text}
          </Typography>
        </Box>
      )}
      {card.charge && (
        <Box
          data-testid="assistant-approval-charge"
          sx={{ p: 1, borderRadius: 1, bgcolor: 'warning.light' }}
        >
          <Typography variant="body2" fontWeight={600}>
            {card.charge.text}
          </Typography>
        </Box>
      )}
      {card.referralPreview && (
        <Box
          data-testid="assistant-approval-referral-preview"
          sx={{ p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}
        >
          <AssistantReferralPreview draft={card.referralPreview} />
        </Box>
      )}
      {card.notes.map(note => (
        <Typography
          key={note}
          variant="caption"
          color={card.irreversible ? 'warning.dark' : 'text.secondary'}
        >
          {note}
        </Typography>
      ))}
      {isPending &&
        !isExpired &&
        card.checkboxes.map(box => (
          <FormControlLabel
            key={box.id}
            sx={{ alignItems: 'flex-start', mx: 0 }}
            control={
              <Checkbox
                size="small"
                sx={{ p: 0.5, mr: 0.5 }}
                checked={Boolean(ticked[box.id])}
                disabled={disabled}
                onChange={(_event, checked) =>
                  setTicked(current => ({ ...current, [box.id]: checked }))
                }
              />
            }
            label={<CheckboxLabel box={box} />}
          />
        ))}
      {isPending && isExpired && (
        <Typography variant="caption" color="text.secondary" role="status">
          This confirmation expired. Ask me again and I’ll show a fresh one.
        </Typography>
      )}
      {isPending ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 0.5,
            '& .MuiButton-root': { py: 0.25, px: 1.25, minWidth: 0 },
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onApprove}
            disabled={disabled || isExpired || !allTicked}
          >
            {card.confirmLabel}
          </Button>
        </Box>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          role="status"
          data-testid="assistant-approval-outcome"
        >
          {OUTCOME_TEXT[phase]}
        </Typography>
      )}
    </Box>
  )
}

export default AssistantApprovalCard
